import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { TECNOLOGIA_SECCIONES_ENDPOINTS } from '@/features/recet/api/endpoints';
import type {
  TecnologiaDetalleDto,
  TecnologiaSeccionListaDto,
  TecnologiaSeccionDetalleDto,
  ActualizarContenidoSeccionCommand,
  AgregarSeccionTecnologiaCommand,
  ReordenarSeccionesTecnologiaCommand,
} from '@/features/recet/types/tecnologia.types';

export function useSeccionesTecnologiaQuery(tecnologiaId: number) {
  return useQuery({
    queryKey: ['tecnologia-secciones', tecnologiaId],
    queryFn: () =>
      apiClient<TecnologiaSeccionListaDto[]>(TECNOLOGIA_SECCIONES_ENDPOINTS.LISTAR(tecnologiaId)),
    enabled: tecnologiaId > 0,
  });
}

export function useSeccionDetalleQuery(tecnologiaId: number, seccionId: number | null) {
  return useQuery({
    queryKey: ['tecnologia-secciones', tecnologiaId, 'detalle', seccionId],
    queryFn: () =>
      apiClient<TecnologiaSeccionDetalleDto>(
        TECNOLOGIA_SECCIONES_ENDPOINTS.OBTENER(tecnologiaId, seccionId!)
      ),
    enabled: tecnologiaId > 0 && seccionId != null && seccionId > 0,
  });
}

export function useActualizarContenidoSeccionMutation(tecnologiaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      seccionId,
      ...data
    }: ActualizarContenidoSeccionCommand & { seccionId: number }) =>
      apiClient<number>(
        TECNOLOGIA_SECCIONES_ENDPOINTS.ACTUALIZAR_CONTENIDO(tecnologiaId, seccionId),
        { method: 'PUT', body: data }
      ),
    onSuccess: (nuevaVersion, variables) => {
      // Update versionActual synchronously to avoid stale version on next save (e.g. section switch)
      qc.setQueryData<TecnologiaDetalleDto>(['recet-tecnologias', tecnologiaId], old =>
        old ? { ...old, versionActual: nuevaVersion } : old
      );
      // Mark the section detalle as stale without refetching. Writing the
      // just-saved content back into the cache here used to cause the editor's
      // prop-sync useEffect to re-run setContent, which jumps the cursor to the
      // end of the doc. Letting the next mount refetch keeps the cache honest
      // without echoing the content into the live editor.
      qc.invalidateQueries({
        queryKey: ['tecnologia-secciones', tecnologiaId, 'detalle', variables.seccionId],
        refetchType: 'none',
      });
      qc.invalidateQueries({ queryKey: ['tecnologia-secciones', tecnologiaId], exact: true });
      qc.invalidateQueries({ queryKey: ['recet-versiones', tecnologiaId] });
    },
  });
}

export function useAgregarSeccionMutation(tecnologiaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AgregarSeccionTecnologiaCommand) =>
      apiClient(TECNOLOGIA_SECCIONES_ENDPOINTS.AGREGAR(tecnologiaId), {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tecnologia-secciones', tecnologiaId] }),
  });
}

export function useEliminarSeccionMutation(tecnologiaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (seccionId: number) =>
      apiClient(TECNOLOGIA_SECCIONES_ENDPOINTS.ELIMINAR(tecnologiaId, seccionId), {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tecnologia-secciones', tecnologiaId] }),
  });
}

export function useReordenarSeccionesMutation(tecnologiaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ReordenarSeccionesTecnologiaCommand) =>
      apiClient(TECNOLOGIA_SECCIONES_ENDPOINTS.REORDENAR(tecnologiaId), {
        method: 'PUT',
        body: data,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tecnologia-secciones', tecnologiaId] }),
  });
}
